import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Namespace } from '../../../translation/namespace';
import { sendLiveRegionMessage } from '../../atoms/LiveRegionGlobal/LiveRegionGlobal';
import { LoadingSpinner } from '../../atoms/LoadingSpinner';
import { RefetchBlock } from '../RefetchBlock';
import { PersonInfoCardModifier, PersonInfoCardData } from './PersonInfoCard.types';
import { PersonInfoCard } from './PersonInfoCard';
import { Modifier } from '../../../lib/bemUtils/Modifier';

type FetchablePersonInfoCard = {
  personInfoCardUrl: string;
  modifier?: Modifier<PersonInfoCardModifier>;
  stylingContent?: 'normal' | 'dark';
  headlineHierarchy?: number;
};

/**
 * Fetches InfoCard Data by Url and renders PersonInfoCard based an that data.
 * This Component encapsulates the fetch logic only.
 */
export function FetchablePersonInfoCard({
  personInfoCardUrl,
  modifier,
  stylingContent = 'dark',
  headlineHierarchy = 2,
}: FetchablePersonInfoCard) {
  const { t } = useTranslation(Namespace.PersonInfoCard);

  const [infoCardData, setInfoCardData] = useState<PersonInfoCardData>();
  const [isCurrentlyLoading, setIsCurrentlyLoading] = React.useState(false);
  // used to retrigger the useEffect to fetch data
  const [refetch, setRefetch] = useState(false);
  const [fetchWasError, setFetchWasError] = useState(false);

  useEffect(() => {
    if (!infoCardData) {
      setIsCurrentlyLoading(true);
      axios
        .get(personInfoCardUrl)
        .then((res) => {
          setIsCurrentlyLoading(false);
          setFetchWasError(false);
          setInfoCardData(res.data);
        })
        .catch(() => {
          setIsCurrentlyLoading(false);
          setFetchWasError(true);
        });
    }
  }, [infoCardData, personInfoCardUrl, refetch]);

  if ((!infoCardData && !fetchWasError) || isCurrentlyLoading) {
    sendLiveRegionMessage(t(`${Namespace.phrases}:contentIsLoading`));
    return (
      <div className="person-info-card">
        <div className="person-info-card__loading-spinner-container">
          <div className="person-info-card__loading-spinner">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  if (fetchWasError) {
    return (
      <div className="person-info-card">
        <RefetchBlock refetchBlock={() => setRefetch(!refetch)} />
      </div>
    );
  }
  return (
    <PersonInfoCard
      modifier={modifier}
      infoCardData={infoCardData}
      stylingContent={stylingContent}
      headlineHierarchy={headlineHierarchy}
    />
  );
}
